import { useContext, useEffect, useState, useReducer } from 'react';
import { QueryHookOptions, useQuery } from '@apollo/react-hooks';
import {
  OutageEvent,
  Query,
  AccountDetail,
  NotificationType,
  Maybe,
  Scalars,
  NotificationFor,
} from '../../__generated__/pge-types';
import {
  getAllOutageEvents,
  getAccountDetails,
} from '../../queries/outage.query';
import { getAccountsList } from '../../components/account-dropdown/queries';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import { OutageMapNotificationContext } from '../../providers/OutageMapNotificationProvider';
import { useTranslation } from '../../hooks/useTranslation';
import useAccountDetailList from '../../hooks/useAccountDetailList';
import AccountListReducer from '../account-summary/multi-accounts/accountListReducer';
import useAuthQuery from '../../hooks/useAuthQuery';
import { NotificationSeverity } from '../outage-map-notifications/OutageMapNotifications';
import useFeatureFlagProtectedValues from '../account-summary/multi-accounts/useFeatureFlagProtectedValues';
import { VirtualAccountType } from '../../constants/customEnum';

type AccountIdentifier = {
  accountNumber?: Scalars['String'];
  encryptedPersonId?: Scalars['String'];
};

type AccountIdentifierList = {
  validAccounts: Array<AccountIdentifier>;
};

export const useOutageEvents = (options?: QueryHookOptions) => {
  const { t } = useTranslation();
  const [lastUpdateOutageDataTime, setLastUpdateOutageDataTime] = useState<
    Maybe<Scalars['DateTimeISO']>
  >('');
  const notificationContext = useContext(NotificationsContext);
  const outageMapNotificationContext = useContext(OutageMapNotificationContext);
  const setErrorNotification = (isError: boolean) => {
    notificationContext.setState({
      isOpen: isError,
      message: isError
        ? t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY')
        : undefined,
      severity: isError ? 'error' : undefined,
    });
    isError && typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const setOutageMapNotification = (
    severity: NotificationSeverity,
    message: string,
  ) => {
    outageMapNotificationContext.setState({
      isOpen: true,
      severity: severity,
      message: t(message),
    });
  };

  const {
    getAccountListStateByClosedAcctFlag,
  } = useFeatureFlagProtectedValues();
  const { data, error, loading, refetch } = useQuery<Query>(
    getAllOutageEvents,
    options,
  );
  useEffect(() => {
    setLastUpdateOutageDataTime(data?.getAllOutageEvents?.lastUpdateTime);
  }, [data]);

  const [accountListParams] = useReducer(AccountListReducer.reducer, {
    ...getAccountListStateByClosedAcctFlag(),
    pageSize: 'All',
  });

  const { accounts } = useAccountDetailList(
    getAccountsList,
    VirtualAccountType.ALL_ACCTS,
    {
      accountListParams,
    },
  );

  const { validAccounts } = accounts.reduce(
    (acc: AccountIdentifierList, account) => {
      if (account.isActive) {
        if (account.isDefault) {
          acc.validAccounts.push({
            accountNumber: account.accountNumber,
            encryptedPersonId: account.encryptedPersonId,
          });
        } else if (acc.validAccounts.length < 5 && !account.isDefault) {
          acc.validAccounts.push({
            accountNumber: account.accountNumber,
            encryptedPersonId: account.encryptedPersonId,
          });
        }
      }
      return acc;
    },
    { validAccounts: [] },
  );

  const accountParams = JSON.parse(JSON.stringify(validAccounts));

  const {
    loading: accountDetailsLoading,
    data: accountDetails,
    refetch: refetchAccountDetails,
  } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetails, {
    variables: {
      params: {
        accountNumberList: accountParams,
      },
      commPreferencesParams: {
        notificationFor: NotificationFor.Outage,
      },
    },
    skip: !accountParams,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    onError: () => {
      setErrorNotification(true);
    },
  });

  return {
    outageData: data?.getAllOutageEvents?.events as OutageEvent[],
    totalCustomersAffected: data?.getAllOutageEvents
      ?.totalCustomersAffected as number,
    accountDetails: accountDetails?.getAccountDetails,
    accountDetailsLoading,
    refetchAccountDetails,
    lastUpdateOutageDataTime,
    error,
    loading,
    refetch,
    setOutageMapNotification,
  };
};
